import { useNavigate, useParams } from "react-router-dom";
import { useProfesional } from "../context/ProfesionalContext";
import { Empleo } from "../types/landingSections";
import { ButtonClose, ButtonPopup, ButtonVolver, Input } from "@/components/ui";
import { Send } from "lucide-react";
import { useEffect, useState } from "react";
import { getEmpleosLanding, updateEmpleosLanding } from "../api/landin";
import Alerta from "@/components/alert/Alerta";
import { uploadEmpleoImage } from "../subirImagen/subirImagen";

const EditarEmpleoLanding = () => {
  const navigate = useNavigate();

  const { landingData, setLandingData, perfilId } = useProfesional();

  const { profesionalId, profesionId } = useParams();

  const [loading, setLoading] = useState(false);

  const [uploadingImages, setUploadingImages] = useState(0);

  const [alerta, setAlerta] = useState<{
    mensaje: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);

  useEffect(() => {
    if (!perfilId) return;

    const cargarEmpleos = async () => {
      try {
        const res = await getEmpleosLanding(perfilId);

        setLandingData((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            home: {
              ...prev.home,
              empleos: res.data,
            },
          };
        });
      } catch (e) {
        console.error(e);
      }
    };

    cargarEmpleos();
  }, [perfilId, setLandingData]);

  if (!landingData || perfilId === null) {
    return null;
  }

  const empleos = landingData.home.empleos ?? [];
  const MAX_EMPLEOS = 4;

  const updateEmpleo = (id: string, field: keyof Empleo, value: any) => {
    setLandingData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        home: {
          ...prev.home,
          empleos: prev.home.empleos.map((e) =>
            e.id === id ? { ...e, [field]: value } : e,
          ),
        },
      };
    });
  };

  const addEmpleo = () => {
    if (empleos.length >= MAX_EMPLEOS) return;

    const newEmpleo: Empleo = {
      id: crypto.randomUUID(),
      titulo: "",
      descripcion: "",
      requisitos: "",
      responsabilidades: "",
      salario: "",
      ubicacion: "",
      modalidad: "",
      imagen: "",
    };

    setLandingData({
      ...landingData,
      home: {
        ...landingData.home,
        empleos: [...empleos, newEmpleo],
      },
    });
  };

  const removeEmpleo = (id: string) => {
    setLandingData({
      ...landingData,
      home: {
        ...landingData.home,
        empleos: empleos.filter((e) => e.id !== id),
      },
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const res = await updateEmpleosLanding(
        perfilId,
        landingData.home.empleos,
      );

      if (res.ok) {
        setAlerta({
          mensaje: "Empleos guardados correctamente.",
          type: "success",
        });
      } else {
        setAlerta({
          mensaje: "No se pudieron guardar los empleos.",
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);

      setAlerta({
        mensaje: "Ha ocurrido un error al guardar los empleos.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    empleoId: string,
  ) => {
    const file = e.target.files?.[0];

    if (!file || perfilId == null) return;

    // Indicamos que empieza una subida
    setUploadingImages((n) => n + 1);

    // Vista previa inmediata
    const preview = URL.createObjectURL(file);
    updateEmpleo(empleoId, "imagen", preview);

    try {
      // Subir a S3
      const { url } = await uploadEmpleoImage(file, perfilId);

      // Sustituir el blob por la URL definitiva
      updateEmpleo(empleoId, "imagen", url);
    } catch (err) {
      console.error(err);

      // Si falla la subida quitamos la vista previa
      updateEmpleo(empleoId, "imagen", "");
    } finally {
      URL.revokeObjectURL(preview);

      // Finaliza una subida
      setUploadingImages((n) => n - 1);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-5 text-white">
      <div className="absolute top-4 left-4 z-50 pointer-events-auto mb-8">
        <ButtonVolver
          onClick={() =>
            navigate(
              `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
            )
          }
        />
      </div>

      <div
        className="
        w-full
        rounded-lg
        border-t-2 border-b-2 border-green-200
        bg-zinc-950
        py-2 px-8
        shadow-md
        shadow-zinc-800
        backdrop-blur-xl
        mb-10
        mt-5
      "
      >
        <div
          className="
          flex flex-col
          gap-8
          md:flex-row
          md:items-center
          md:justify-between
        "
        >
          {/* INFO */}
          <div className="space-y-3 flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              Editor de Empleos
            </h1>

            <p className="text-zinc-400 text-sm max-w-md">
              Configura las ofertas laborales de tu landing.
            </p>
          </div>

          {/* BOTONES */}
          <div
            className="
            flex
            flex-col
            sm:flex-row
            sm:justify-end
            sm:items-center
            gap-3
            w-full
            md:w-auto
          "
          >
            <ButtonPopup
              onClick={addEmpleo}
              disabled={empleos.length >= MAX_EMPLEOS}
              className="
              px-4 py-2
              rounded-lg
              bg-zinc-900
              border border-white/10
              hover:border-green-500
              transition
              flex items-center justify-center
              whitespace-nowrap
              overflow-hidden
              text-ellipsis
              max-w-full
            "
            >
              {empleos.length >= MAX_EMPLEOS
                ? "Máximo alcanzado"
                : "Añadir empleo"}
            </ButtonPopup>

            <ButtonPopup
              onClick={handleSave}
              disabled={loading || uploadingImages > 0}
              className="
              px-4 py-2
              rounded-lg
              bg-zinc-900
              border border-white/10
              hover:border-cyan-500
              transition
              flex items-center justify-center
              whitespace-nowrap
              overflow-hidden
              text-ellipsis
              max-w-full
            "
            >
              {uploadingImages > 0
                ? "Subiendo imágenes..."
                : loading
                  ? "Guardando..."
                  : "Guardar empleo"}
            </ButtonPopup>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <div
          className="
          grid
          grid-cols-1
          sm:grid-cols-2
          md:grid-cols-4
          xl:grid-cols-5
          gap-6
          w-full
          max-w-7xl
        "
        >
          {empleos.map((empleo) => (
            <div
              key={empleo.id}
              className="
          w-full
          bg-white/10
          backdrop-blur-2xl
          border-r-4 border-t-2 border-b-2 border-zinc-700
          rounded-lg
          overflow-hidden
          shadow-md shadow-zinc-800
        "
            >
              <div className="relative h-[120px]">
                <img
                  src={empleo.imagen || "/img/logo/LogoLinkServs.png"}
                  className="w-full h-full object-cover"
                  alt={empleo.titulo || "Empleo"}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                <div className="absolute bottom-2 left-2 right-2 space-y-2">
                  <input
                    value={empleo.titulo ?? ""}
                    placeholder="Título"
                    onChange={(e) =>
                      updateEmpleo(empleo.id!, "titulo", e.target.value)
                    }
                    className="w-full bg-black/30 border border-pink-500/60 rounded-lg px-2 py-1.5 text-[12px] text-white"
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={empleo.ubicacion ?? ""}
                      placeholder="Ubicación"
                      onChange={(e) =>
                        updateEmpleo(empleo.id!, "ubicacion", e.target.value)
                      }
                      className="bg-black/30 border border-pink-500/60 rounded-lg px-2 py-1.5 text-[11px] text-white"
                    />

                    <input
                      value={empleo.modalidad ?? ""}
                      placeholder="Modalidad"
                      onChange={(e) =>
                        updateEmpleo(empleo.id!, "modalidad", e.target.value)
                      }
                      className="bg-black/30 border border-pink-500/60 rounded-lg px-2 py-1.5 text-[11px] text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="p-3 space-y-2">
                <textarea
                  value={empleo.descripcion ?? ""}
                  placeholder="Descripción"
                  onChange={(e) =>
                    updateEmpleo(empleo.id!, "descripcion", e.target.value)
                  }
                  className="w-full h-16 resize-none bg-zinc-900 border border-white/10 rounded-lg px-2 py-2 text-[12px]"
                />

                <textarea
                  value={empleo.requisitos ?? ""}
                  placeholder="Requisitos"
                  onChange={(e) =>
                    updateEmpleo(empleo.id!, "requisitos", e.target.value)
                  }
                  className="w-full h-16 resize-none bg-zinc-900 border border-white/10 rounded-lg px-2 py-2 text-[12px]"
                />

                <textarea
                  value={empleo.responsabilidades ?? ""}
                  placeholder="Responsabilidades"
                  onChange={(e) =>
                    updateEmpleo(
                      empleo.id!,
                      "responsabilidades",
                      e.target.value,
                    )
                  }
                  className="w-full h-16 resize-none bg-zinc-900 border border-white/10 rounded-lg px-2 py-2 text-[12px]"
                />

                <Input
                  value={empleo.salario ?? ""}
                  placeholder="Salario"
                  onChange={(e) =>
                    updateEmpleo(empleo.id!, "salario", e.target.value)
                  }
                />

                <label className="w-full flex items-center justify-center px-3 py-2 rounded-md bg-zinc-900 border border-white/10 hover:border-pink-500/50 transition cursor-pointer text-[12px] text-white/80">
                  {empleo.imagen ? "Cambiar imagen" : "Seleccionar imagen"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, empleo.id!)}
                  />
                </label>

                <div className="flex flex-col gap-2 pt-2 border-t border-white/10">
                  <span className="px-3 py-2 bg-cyan-500/20 border border-cyan-400/30 rounded-lg text-[11px] text-center">
                    {empleo.salario || "Salario a convenir"}
                  </span>

                  <button className="flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-[12px] font-semibold">
                    <Send size={14} />
                    Insertar CV
                  </button>
                </div>

                <div className="flex justify-end pt-1">
                  <ButtonClose onClick={() => removeEmpleo(empleo.id!)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}
    </div>
  );
};

export default EditarEmpleoLanding;